import React, { useState } from 'react';
import { Box, Avatar, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { getAvatarComponent } from '../../utils/getAvatarComponent.tsx';

// Define paths for PNG avatar images
const AVATAR_IMAGES = {
  Alpha: '/images/avatars/Alpha.png',
  Beta: '/images/avatars/Beta.png',
  Gamma: '/images/avatars/Gamma.png',
  Delta: '/images/avatars/Delta.png',
  Modelistic: '/images/avatars/Modelistic.png',
  Eterna: '/images/avatars/Eterna.png'
};

interface SurrogateAvatarProps {
  name: string;
  role?: string;
  image?: string;
  status?: 'active' | 'idle' | 'busy';
  size?: 'small' | 'medium' | 'large';
  showName?: boolean;
}

// Styled components
const AvatarContainer = styled(Box)<{ size: string }>(({ theme, size }) => {
  const sizeMap = {
    small: 40,
    medium: 60,
    large: 80,
  };
  
  const containerSize = sizeMap[size as keyof typeof sizeMap] || 60;
  
  return {
    position: 'relative',
    width: containerSize,
    height: containerSize,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  };
});

const StyledAvatar = styled(Avatar)<{ size: string }>(({ theme, size }) => {
  const sizeMap = {
    small: 40,
    medium: 60,
    large: 80,
  };
  
  const avatarSize = sizeMap[size as keyof typeof sizeMap] || 60;
  
  return {
    width: avatarSize,
    height: avatarSize,
    backgroundColor: 'rgba(26, 26, 46, 0.7)',
    border: '2px solid rgba(138, 43, 226, 0.5)',
    boxShadow: '0 0 15px rgba(138, 43, 226, 0.3)',
  };
});

const StatusIndicator = styled(Box)<{ status: string; size: string }>(({ theme, status, size }) => {
  const sizeMap = {
    small: 10,
    medium: 14,
    large: 18,
  };
  
  const indicatorSize = sizeMap[size as keyof typeof sizeMap] || 14;
  
  let statusColor = '#00CC99'; // active (default)
  if (status === 'idle') statusColor = '#FFA500';
  if (status === 'busy') statusColor = '#FF4500';
  
  return {
    position: 'absolute',
    bottom: 0,
    right: 0,
    width: indicatorSize,
    height: indicatorSize,
    borderRadius: '50%',
    backgroundColor: statusColor,
    border: '2px solid rgba(26, 26, 46, 0.9)',
    zIndex: 2,
  };
});

const NameText = styled(Typography)<{ size: string }>(({ theme, size }) => ({
  fontSize: size === 'small' ? '0.8rem' : size === 'large' ? '1.1rem' : '0.9rem',
  fontWeight: 600,
  color: '#fff',
  marginTop: theme.spacing(1),
  textAlign: 'center',
}));

const RoleText = styled(Typography)<{ size: string }>(({ theme, size }) => ({
  fontSize: size === 'small' ? '0.7rem' : size === 'large' ? '0.9rem' : '0.8rem',
  color: 'rgba(255, 255, 255, 0.7)',
  textAlign: 'center',
}));

// Get avatar content based on image prop
const getAvatarContent = (
  image: string | undefined, 
  name: string, 
  size: number
) => {
  // Check if the image is one of our PNG avatars
  if (image && AVATAR_IMAGES[image as keyof typeof AVATAR_IMAGES]) {
    // Return null so the Avatar component uses the src prop
    return null;
  }
  
  if (!image) {
    // If no image, return initials
    return getInitials(name);
  }

  const avatarComponent = getAvatarComponent(image, '#8A2BE2', size);
  
  // If it's a React component (SVG avatar), return it
  if (typeof avatarComponent !== 'string') {
    return avatarComponent;
  }
  
  // If it's a string (image path), return null so the Avatar component uses the src prop
  return null;
};

// Get initials from name
const getInitials = (name: string) => {
  return name
    .split(' ')
    .map(part => part[0])
    .join('')
    .toUpperCase()
    .substring(0, 2);
};

const SurrogateAvatar: React.FC<SurrogateAvatarProps> = ({
  name,
  role,
  image,
  status,
  size = 'medium',
  showName = false,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  
  // Determine if we should use a PNG image
  const isPngAvatar = image && AVATAR_IMAGES[image as keyof typeof AVATAR_IMAGES];
  
  // Get the avatar content (SVG component or initials)
  const avatarContent = getAvatarContent(image, name, size === 'small' ? 24 : size === 'large' ? 48 : 36);

  // Get the image source
  const imageSrc = isPngAvatar 
    ? AVATAR_IMAGES[image as keyof typeof AVATAR_IMAGES] 
    : undefined;

  return (
    <AvatarContainer 
      size={size}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <StyledAvatar 
        src={imageSrc}
        alt={name} 
        size={size}
        sx={{ 
          transform: isHovered ? 'scale(1.05)' : 'scale(1)',
          transition: 'transform 0.3s ease',
          boxShadow: isHovered ? '0 0 20px rgba(138, 43, 226, 0.5)' : '0 0 15px rgba(138, 43, 226, 0.3)'
        }}
      >
        {avatarContent}
      </StyledAvatar>
      
      {status && <StatusIndicator status={status} size={size} />}
      
      {showName && (
        <Box sx={{ mt: 1 }}>
          <NameText size={size}>{name}</NameText>
          {role && <RoleText size={size}>{role}</RoleText>}
        </Box>
      )}
    </AvatarContainer>
  );
};

export default SurrogateAvatar; 